import type { ChangeEvent } from 'react'
import { useCallback } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Card, CardDescription, CardTitle } from '@/components/ui/card.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { SubscribersAnalysisDataUI } from '@/modules/tracking/new-subscriber-analysis/types.ts'
import { getPercentValue } from '@/utils/helpers.ts'

const FIELD_ID = 'SpendingCustomerConversion'

interface Props {
  title: string
  description: string
  donateHighType?: boolean
  subscribersAnalysis: SubscribersAnalysisDataUI | null
  spendingFansCount: number
  total: number
  limit: number
  isFetching: boolean
  setSpendCustomerListDialogOpen: (value: boolean) => void
  setLimit: (event: ChangeEvent<HTMLInputElement>) => void
}
export const SpendingCustomerConversion = ({
  title,
  description,
  donateHighType,
  spendingFansCount,
  total,
  limit,
  setLimit,
  isFetching,
  setSpendCustomerListDialogOpen,
}: Props) => {
  const handleViewListButtonClick = useCallback(() => {
    setSpendCustomerListDialogOpen(true)
  }, [setSpendCustomerListDialogOpen])

  return (
    <Card className="grid gap-6 px-8 py-6">
      <div className="grid">
        <CardTitle className="mb-2 flex justify-between gap-2">
          <div className="text-md md:text-2xl">{title}</div>

          <TextField
            id={FIELD_ID}
            prefix="$"
            type="number"
            value={limit}
            onChange={setLimit}
            suffix={donateHighType ? 'Min' : 'Max'}
            className="flex h-8 w-24 min-w-24 flex-nowrap gap-1 rounded px-2  py-1 text-sm leading-6 text-secondary-300 dark:bg-[#3B4056]"
          />
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>

      {isFetching ? (
        <div className="flex w-full flex-wrap items-center gap-6 md:flex-nowrap md:justify-between">
          <div className="flex h-11 w-3/12 animate-pulse items-center gap-3 rounded-md bg-skeleton" />
          <div className="flex h-11 w-3/12 animate-pulse items-center gap-3 rounded-md bg-skeleton" />
          <div className="flex h-11 w-3/12 animate-pulse items-center gap-3 rounded-md bg-skeleton" />
        </div>
      ) : (
        <div className="flex w-full flex-wrap items-center gap-6 md:flex-nowrap md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 min-w-16 items-center justify-center rounded bg-yellow-100 font-semibold text-secondary-500">
              {spendingFansCount}
            </div>
            <div className="text-sm">
              {donateHighType ? 'High' : 'Low'}-Spending Customer
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 min-w-16 items-center justify-center rounded bg-blue-200 font-semibold text-secondary-500">
              {getPercentValue(spendingFansCount / total) || 0}
            </div>
            <div className="text-sm">Percentage of all new subscribers</div>
          </div>
          <Button onClick={handleViewListButtonClick}>View List</Button>
        </div>
      )}
    </Card>
  )
}
